import { newClientId } from './clientId'
import type { OutgoingPhoto } from './core'

/** The longest side a photo is sent at, in pixels. */
const MAX_PIXEL_SIZE = 2048
const JPEG_QUALITY = 0.8

/** The longest-side cap applied to a photo's size, keeping its shape. */
export const fittedSize = (width: number, height: number, max = MAX_PIXEL_SIZE): { width: number; height: number } => {
	const scale = Math.min(1, max / Math.max(width, height))
	return { width: Math.max(1, Math.round(width * scale)), height: Math.max(1, Math.round(height * scale)) }
}

/**
 * A picked photo scaled down, its orientation applied, as a JPEG kept in the browser
 * for the send to upload — as iOS's photo picker prepares them. Null when the browser
 * can't read the image.
 */
export const preparePhoto = async (file: File): Promise<OutgoingPhoto | null> => {
	try {
		// `createImageBitmap` applies EXIF orientation, so a portrait shot stays upright.
		const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
		const size = fittedSize(bitmap.width, bitmap.height)
		const canvas = document.createElement('canvas')
		canvas.width = size.width
		canvas.height = size.height
		const context = canvas.getContext('2d')
		if (!context) return null
		context.drawImage(bitmap, 0, 0, size.width, size.height)
		bitmap.close()
		const jpeg = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
		if (!jpeg) return null
		return { id: newClientId(), file: jpeg, url: URL.createObjectURL(jpeg), ...size }
	} catch {
		return null
	}
}
